import { Alerts } from 'constants/alerts';
import { CommissionStatuses } from 'constants/commissionStatuses';
import { CommissionTypes } from 'constants/commissionTypes';
import { Resources } from 'constants/resources';

import { useEffect } from 'react';

import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import { useApiContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { CommissionsDeleteDialog } from 'pages/Commissions/components/CommissionsDeleteDialog';
import { RecordContextProvider, useListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { ContextMenuEditButton } from 'shared/mui/ContextMenu/ContextMenuEditButton/ContextMenuEditButton';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { DatagridBody } from 'shared/react-admin/DatagridBodies/DatagridBody/DatagridBody';
import { DatagridCommissionsRow } from 'shared/react-admin/DatagridRows/DatagridCommissionsRow/DatagridCommissionsRow';
import { DatagridRow } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';
import { DatagridStickyContainer } from 'shared/react-admin/Datagrids/DatagridSticky/DatagridStickyContainer';
import {
  ChipField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  SingleFieldList,
  TextField,
} from 'shared/react-admin/Fields';
import { CommissionStatusChipField } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField';
import { IconButtonStyled } from 'shared/react-admin/Fields/CommissionStatusChipField/CommissionStatusChipField.styled';
import { TooltipTextLinkField } from 'shared/react-admin/Fields/TooltipTextLinkField/TooltipTextLinkField';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { ReferenceArrayField } from 'shared/react-admin/ReferenceFields';
import { CommissionsCommentDialog } from 'shared/widgets/CommissionsCommentDialog/CommissionsCommentDialog';
import { Commission } from 'types/embedded/commission';
import { Currency } from 'types/embedded/currency';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';
import { clearLSTemporary } from 'utils/clearLSTemporary';

import { DatagridStyled } from './CommissionsListDatagrid.styled';
import { CommissionsListDatagridRateField } from './CommissionsListDatagridRateField';
import { COMMISSIONS_PREFERENCE_KEY } from '../CommissionsListContent';

export const CommissionsListDatagrid: React.FC = () => {
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const { appliedFilters } = useFilterContext();
  const { data, isLoading, filterValues } = useListContext();
  const { user } = useApiContext();

  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<Commission>();

  const {
    record: recordComment,
    handleOpenRecordModal: handleOpenRecordModalComment,
    openRecordModal: openRecordModalComment,
    handleCloseRecordModal: handleCloseRecordModalComment,
  } = useRecordModal<Commission>();

  useSyncFilter({
    appliedFilters,
    transform: (appliedFilters) => {
      const { status: fullIdStatus = [], ...restAppliedFilters } = appliedFilters;
      return cleanEmpty({
        ...restAppliedFilters,
        status: fullIdStatus?.map((status: string) => getId(status)),
        'parent.id': appliedFilters?.parent || null,
        'currency.id': appliedFilters?.currency || [],
        ignoreParentFilter:
          !!appliedFilters?.parent || !!appliedFilters?.status?.length || !!appliedFilters?.name,
      });
    },
  });

  useEffect(() => {
    clearLSTemporary('isNotFirstCommissionsRender1', Resources.Commissions, () =>
      localStorage.removeItem('isNotFirstCommissionsRender1'),
    );
  }, []);

  if (user?.alerts.includes(Alerts.EmptyProviderAccountAccess)) {
    return (
      <Alert severity="warning">{`Для пользователя ${user.firstName} ${user.lastName} отсутствует доступ к настройкам "Комиссий", обратитесь к руководителю вашей рабочей группы для предоставления доступа.`}</Alert>
    );
  }

  if (user?.alerts.includes(Alerts.EmptyMerchantAccess)) {
    return (
      <Alert severity="warning">{`Для пользователя ${user.firstName} ${user.lastName} нет доступных мерчантов, обратитесь к администратору системы для настроек доступа.`}</Alert>
    );
  }

  if (!data?.length && !isLoading) {
    return (
      <Alert severity="info">
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Нет созданных "Комиссий" для доступных "Мерчантов" и "Провайдеров"
      </Alert>
    );
  }

  return (
    <>
      <DatagridStickyContainer>
        <DatagridStyled
          body={
            <DatagridBody
              row={
                !filterValues?.id && !filterValues?.status?.length ? (
                  <DatagridCommissionsRow />
                ) : (
                  <DatagridRow />
                )
              }
            />
          }
          bulkActionButtons={false}
          preferenceKey={COMMISSIONS_PREFERENCE_KEY}
        >
          <TextField label="ID" sortBy="id" source="originId" />
          <FunctionField
            label="Тип комиссии"
            render={(record: Commission) => {
              if (record?.typeEnum?.value === CommissionTypes.Child) {
                return `➥ ${record.typeEnum.description}`;
              }
              return record?.typeEnum?.description;
            }}
            sortable={false}
            source="typeEnum"
            sx={{ textWrap: 'nowrap' }}
          />
          <EmbeddedFunctionField
            cellClassName="column-ladderType"
            label="Тип лесенки"
            sortable={false}
            sourceField="ladder.ladderType.description"
          />
          <TooltipTextLinkField label="Название" resource="commissions" source="name" type="show" />
          <FunctionField
            label="Статус"
            render={(record: Commission) => {
              return (
                <RecordContextProvider value={record?.status}>
                  <CommissionStatusChipField source="description">
                    {Boolean(record?.commentCount) && (
                      <IconButtonStyled onClick={() => handleOpenRecordModalComment(record)}>
                        <ModeCommentOutlinedIcon />
                      </IconButtonStyled>
                    )}
                  </CommissionStatusChipField>
                </RecordContextProvider>
              );
            }}
            source="status"
          />
          {hasResourceAccess(Resources.Merchants) && (
            <EmbeddedFunctionField
              cellClassName="column-merchant"
              label="Мерчант"
              linkConfig={{
                resource: Resources.Merchants,
                idField: 'merchant.@id',
              }}
              sortBy="merchant.name"
              sourceField="merchant.name"
            />
          )}
          {hasResourceAccess(Resources.Providers) && (
            <EmbeddedFunctionField
              cellClassName="column-provider"
              label="Провайдер"
              sortBy="provider.name"
              sourceField="provider.name"
            />
          )}
          {hasResourceAccess(Resources.Currencies) && (
            <EmbeddedFunctionField
              cellClassName="column-currency"
              label="Валюта"
              render={(record: Currency) => `${record?.alphaCode} / ${record?.name}`}
              sortBy="currency.alphaCode"
              sourceField="currency"
            />
          )}
          {hasResourceAccess('transaction_types') && (
            <ReferenceArrayField
              label="Типы транзакций"
              reference="transaction_types"
              sortable={false}
              source="transactionTypes"
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          )}
          <CommissionsListDatagridRateField
            cellClassName="column-rate"
            label="Процент, % / Фикс. / Макс. / Мин."
            sx={{ textWrap: 'nowrap' }}
          />
          <EmbeddedFunctionField
            cellClassName="column-calculationMethod"
            label="Способ расчёта"
            sortable={false}
            sourceField="calculationMethod.description"
          />
          <DateTimeField label="Дата создания" source="createdAt" />
          <DateTimeField label="Дата редактир." source="updatedAt" />
          <ContextMenu
            render={(record: Commission) => {
              const isShowButton = record?.status
                ? [CommissionStatuses.Created, CommissionStatuses.Rejected].includes(
                    record?.status?.value,
                  ) && record?.typeEnum?.value !== CommissionTypes.Ladder
                : false;
              return [
                hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton
                    entityName="commission"
                    key="history-button"
                  />
                ),
                isShowButton &&
                  hasResourcePermissions(Resources.Commissions, [
                    Permission.Full,
                    Permission.Update,
                  ]) && <ContextMenuEditButton key="edit-button" />,
                <ContextMenuShowButton key="show-button" />,
                isShowButton &&
                  hasResourcePermissions(Resources.Commissions, [
                    Permission.Full,
                    Permission.Delete,
                  ]) && (
                    <ContextMenuDeleteButton
                      key="delete-button"
                      onClick={() => handleOpenRecordModalDelete(record)}
                    />
                  ),
              ];
            }}
          />
        </DatagridStyled>
      </DatagridStickyContainer>
      <Pagination />
      {openRecordModalDelete && (
        <CommissionsDeleteDialog
          onClose={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          record={recordDelete}
        />
      )}
      <CommissionsCommentDialog
        onClose={handleCloseRecordModalComment}
        open={openRecordModalComment}
        record={recordComment}
      />
    </>
  );
};
